import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {useEffect, useState} from 'react';
import _ from '@lodash';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import GroupModel from 'app/shared-components/channel/GroupModel';
import { APIModel } from 'app/shared-components/channel/GroupModel';
import { addTopic, updateTopic } from 'app/store/groupsSlice';
import {
    fetchMembersByChannelId,
    selectAllMembersByChannelId,
    selectChannelById, selectFetchedAllMembersByChannelId, selectLastMemberIdByChannelId,
    selectMembersByChannelIdAndRole, selectMembersStatusByChannelId
} from 'app/store/channelsSlice';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
import EntityForm from 'app/shared-components/layout/EntityForm';
import ControlledSingleSelect from "app/shared-components/form/ControlledSingleSelect";
import ControlledMultiSelect from "app/shared-components/form/ControlledMultiSelect";



/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
  description: yup.string().required('You must enter a description'),
    membershipType: yup.string()
        .required('You must select a membership type')
        .default('inherit'),
    selectedMembers: yup.array().when('membershipType', {
        is: 'custom',
        then: () => yup.array()
            .min(1, 'At least one member must be selected for custom membership')
            .required('Members are required for custom membership')
            .default([]),
        otherwise: () => yup.array().nullable().default([])
    })
});

function TopicForm({type}) {
  const routeParams = useParams();
  const groupId = routeParams.groupId;
  const topicId = routeParams.topicId;
  const isNew = topicId === 'new';
  const topicChannel= isNew ? null : useSelector((state) => selectChannelById(state, topicId));
    const topicMembers = isNew ? [] : useSelector(selectAllMembersByChannelId(topicId));
    const topicMemberStatus= useSelector(selectMembersStatusByChannelId(topicId));
    const topic = isNew ? GroupModel({type: "topic"}) : topicChannel.entity;
    const dispatch = useDispatch();
  const title= isNew ? "New Topic" : "Edit " + topic?.name;
  const returnPath= "/apps/" + type + "/" + groupId +"/topics";
    const allMembers= useSelector(selectAllMembersByChannelId(groupId));
    const guestMembers= useSelector(selectMembersByChannelIdAndRole(groupId, 'guest'));
    const guestStatus= useSelector(selectMembersStatusByChannelId(groupId, 'guest'));
    const guestIsLoading = guestStatus === 'loading';
  const displayMembershipOptions= type === 'organization' || type === 'team';
  const memberOptions= displayMembershipOptions && allMembers ?
      allMembers.map(member => ({
          value: member.id,
          name: member.firstName + " " + member.lastName,
      }))
      : [];
  const guestMemberOptions= displayMembershipOptions && guestMembers ?
    guestMembers.map(member => ({
        value: member.id,
        name: member.firstName + " " + member.lastName,
    }))
    : [];

    const currentlySelectedMembers= !isNew && displayMembershipOptions && topicMembers ?
        topicMembers.map(member => ({
            value: member.id,
            name: member.firstName + " " + member.lastName,
        })) :
        [];

    const currentlySelectedMemberValues=
        currentlySelectedMembers.map(member => member.value);

    const currentlySelectedMembershipType = isNew ? 'inherit' :
        topic.inheritAdminMembers ? 'inheritAdmin' :
            !topic.inheritMembers && currentlySelectedMembers.length > 0 ? 'custom' :
                topic.inheritMembers && currentlySelectedMembers.length > 0 ? 'guest' :
                'inherit';

    const defaultValues = {
        name: '',
        description: '',
        membershipType: currentlySelectedMembershipType,  // Setting inherit as the default membership type
        selectedMembers: currentlySelectedMemberValues
    };

  const { control, watch, reset, handleSubmit, formState, getValues } = useForm({
	defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;

    // Watch the membershipType field to conditionally render selectedMembers
    const membershipType = watch('membershipType');

    useEffect(() => {
        reset({
            ...topic,
            selectedMembers: currentlySelectedMemberValues || [],
            membershipType: currentlySelectedMembershipType
        });
    }, [ topicId, JSON.stringify(currentlySelectedMemberValues), currentlySelectedMembershipType, reset]);

    useEffect(() => {
        if (topicMemberStatus === 'idle'){
            dispatch(fetchMembersByChannelId({channelId: topicId}));
        }
    }, [ topicId, topicMemberStatus ]);

    useEffect(() => {
        if (guestStatus === 'idle'){
            dispatch(fetchMembersByChannelId({channelId: groupId, role: 'guest'}));
        }
    }, [ groupId, guestStatus ]);

  /**
   * Form Submit
   */
  function onSubmit(data) {
	const channel= processFormData(data);
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: updateTopic({type: type, topic: channel}), 
	                returnPath: returnPath, 
	                successMessage:  "Topic Updated!"});
  }

  function onSubmitNew(data) {
	const channel= processFormData(data);
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: addTopic({type: type, topic: channel}), 
	                returnPath: returnPath, 
	                successMessage:  "Topic Created!"});
  }

  function processFormData(data) {
        // Create base topic data
        const topicData = {
            ...data,
            id: isNew ? undefined : topic.id,
            parentChannelId: groupId,
            type: 'topic',
            membershipType: undefined,
            inheritMembers: !displayMembershipOptions,
            inheritAdminMembers: false,
        };

        // Add membership-related flags based on membershipType
      if (displayMembershipOptions)
        switch (data.membershipType) {
            case 'inherit':
                topicData.inheritMembers = true;
                topicData.selectedMembers = [];
                break;
            case 'inheritAdmin':
                topicData.inheritAdminMembers = true;
                topicData.selectedMembers = [];
                break;
            case 'custom':
                // Validate that selectedMembers exists when custom is selected
                if (!topicData.selectedMembers || topicData.selectedMembers.length === 0) {
                    throw new Error('Selected members are required when using custom membership type');
                }
                break;
            case 'guest':
                topicData.inheritMembers = true;
                break;
            default:
                throw new Error('Invalid membership type');
        }

        return APIModel(GroupModel(topicData));
    }

  return (
    <EntityForm title={title} 
	            submitTitle={isNew ? "Create" : "Update"} 
	            onSubmit={isNew ? onSubmitNew : onSubmit}
	            handleSubmit={handleSubmit} isValid={isValid} dirtyFields={dirtyFields}
	            
	>  
		<ControlledTextField name="name" label="Topic name"
                             control={control} errors={errors}
             				 autoFocus required fullWidth />
        
        <ControlledTextField name="description" label="Topic description"
                             control={control} errors={errors}
             				 required fullWidth multiline
             				 minRows={3} maxRows={10} />

        {displayMembershipOptions && (
            <ControlledSingleSelect
                name="membershipType"
                label="Who can access this topic?"
                control={control}
                errors={errors}
                required
                items={[
                    { value: 'inherit', name: 'All Members (not including Guests)' },
                    { value: 'inheritAdmin', name: 'Admin Members' },
                    { value: 'custom', name: 'Only Specific Members' },
                    { value: 'guest', name: 'All Members + Specific Guests' },
                ]}
            />
        )}

        {(membershipType === 'custom' || membershipType === 'guest') && displayMembershipOptions && (
            <ControlledMultiSelect
                name="selectedMembers"
                label="Select Members"
                control={control}
                errors={errors}
                required
                fullWidth
                items={membershipType === 'guest' ? guestMemberOptions : memberOptions}
            />
        )}
    </EntityForm>
  );
}

export default TopicForm;
